import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 💰 Wiki - Seção Saques
 * 
 * Documentação completa sobre o sistema de saques da plataforma.
 * Explica como solicitar saques, acompanhar status e entender os saldos.
 * Voltada para produtores e afiliados (não técnica).
 */
const SaqueWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:cash-multiple" />
          <span>Sistema de Saques</span>
        </div>
        <p className={styles.heroText}>
          Gerencie seus saques de forma simples e segura. Solicite transferências 
          para sua conta bancária e acompanhe todo o processo em tempo real.
        </p>
      </section>

      {/* O que são Saques */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são Saques?</span>
          <p className={styles.sectionSubtitle}>
            Transferência do seu saldo para sua conta bancária
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>Saques</strong> são transferências do seu saldo disponível na plataforma 
              para sua conta bancária cadastrada. Você pode solicitar saques sempre que tiver 
              saldo suficiente e uma conta bancária cadastrada.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              O processo é <strong>simples e seguro</strong>: você solicita o saque, aguarda 
              a aprovação do administrador, e o valor é transferido para sua conta. 
              Tudo fica registrado no histórico!
            </p>
          </div>
        </div>
      </section>

      {/* Como Acessar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar a Página de Saques?</span>
          <p className={styles.sectionSubtitle}>
            Encontre no menu lateral ou no Dashboard
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Financeiro → Saques</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:view-dashboard" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Dashboard
                </span>
                <p>
                  No <strong>Dashboard</strong>, clique no botão <strong>"Solicitar Saque"</strong> 
                  dentro do card <strong>"Saldo para Saque"</strong>. Ou acesse diretamente 
                  <strong>Financeiro → Saques</strong>.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Entendendo os Saldos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Entendendo os Saldos</span>
          <p className={styles.sectionSubtitle}>
            Diferentes tipos de saldo e o que cada um significa
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Disponível</div>
                <span className={styles.cardTitle}>Disponível para Saque</span>
                <p>
                  Este é o <strong>saldo que você pode sacar imediatamente</strong>. 
                  É calculado automaticamente com base nas suas comissões já liberadas.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:check-circle" />
                  <span><strong>Exemplo:</strong> R$ 1.500,00 disponível = pode sacar agora</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:clock-time-four-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Em Análise</div>
                <span className={styles.cardTitle}>Saldo em Análise</span>
                <p>
                  Este é o saldo que está <strong>aguardando liberação</strong> (período D+). 
                  Ainda não está disponível para saque, mas será liberado em breve.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:clock-outline" />
                  <span><strong>Exemplo:</strong> R$ 500,00 em análise = aguardando liberação</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:wallet" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Total</div>
                <span className={styles.cardTitle}>Saldo Total</span>
                <p>
                  É a <strong>soma de todos os seus saldos</strong>: disponível + em análise. 
                  Representa todo o dinheiro que você tem na plataforma.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> R$ 1.500 + R$ 500 = R$ 2.000,00 total</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:cash-check" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Histórico</div>
                <span className={styles.cardTitle}>Total já Sacado</span>
                <p>
                  Este é o <strong>valor total que você já sacou</strong> desde que começou 
                  a usar a plataforma. É apenas para referência histórica.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:history" />
                  <span><strong>Exemplo:</strong> R$ 5.000,00 já sacados = histórico completo</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Solicitar um Saque */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Solicitar um Saque?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo completo para solicitar seu saque
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:bank" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Cadastre uma Conta Bancária
                </span>
                <p>
                  Antes de solicitar um saque, você precisa ter <strong>pelo menos uma conta bancária 
                  cadastrada</strong> em seu nome. Acesse <strong>"Contas Bancárias"</strong> no menu 
                  para cadastrar.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cash" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Verifique o Saldo Disponível
                </span>
                <p>
                  Certifique-se de que você tem <strong>saldo suficiente</strong> para saque. 
                  O valor mínimo é <strong>R$ 10,00</strong>. Você só pode sacar o que está 
                  disponível (não o que está em análise).
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Clique em "Solicitar Saque"
                </span>
                <p>
                  Na página de saques, clique no botão <strong>"Solicitar Saque"</strong> no card 
                  <strong>"Disponível para Saque"</strong>. Um modal será aberto.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:form-select" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Preencha os Dados
                </span>
                <p>
                  Selecione a <strong>conta bancária</strong> de destino, informe o <strong>valor 
                  que deseja sacar</strong> (mínimo R$ 10,00) e adicione uma <strong>observação 
                  opcional</strong> se necessário.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>5</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Confirme a Solicitação
                </span>
                <p>
                  Revise todas as informações e clique em <strong>"Confirmar Solicitação"</strong>. 
                  Sua solicitação será enviada para análise do administrador.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>6</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde a Aprovação
                </span>
                <p>
                  Após a solicitação, o administrador irá <strong>analisar e aprovar</strong> seu saque. 
                  Você receberá uma <strong>notificação</strong> quando o saque for processado.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Status dos Saques */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Status dos Saques</span>
          <p className={styles.sectionSubtitle}>
            Entenda o que significa cada status da sua solicitação
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Em Análise</div>
                <span className={styles.cardTitle}>Pending (Em Análise)</span>
                <p>
                  Sua solicitação foi enviada e está <strong>aguardando análise</strong> do administrador. 
                  Este é o status inicial de todas as solicitações.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:check-circle-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Aprovado</div>
                <span className={styles.cardTitle}>Approved (Aprovado)</span>
                <p>
                  O administrador <strong>aprovou sua solicitação</strong>. O saque está sendo 
                  processado e o valor será transferido em breve.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:cash-check" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Processado</div>
                <span className={styles.cardTitle}>Processed (Processado)</span>
                <p>
                  O saque foi <strong>processado com sucesso</strong> e o valor foi transferido 
                  para sua conta bancária. Verifique seu extrato bancário.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconDanger}`}>
                <Icon icon="mdi:close-circle-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeDanger}`}>Rejeitado</div>
                <span className={styles.cardTitle}>Rejected (Rejeitado)</span>
                <p>
                  O administrador <strong>rejeitou sua solicitação</strong>. O valor retorna 
                  para seu saldo disponível. Verifique a observação para entender o motivo.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Histórico de Saques */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Histórico de Saques</span>
          <p className={styles.sectionSubtitle}>
            Acompanhe todas as suas solicitações de saque
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:history" className={styles.infoIcon} />
          <div>
            <p>
              Na página de saques, você encontra uma <strong>tabela completa</strong> com todo 
              o histórico de suas solicitações. Cada linha mostra:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>ID:</strong> Número único da solicitação</li>
              <li><strong>Data:</strong> Quando você solicitou o saque</li>
              <li><strong>Conta:</strong> Para qual conta bancária o saque foi solicitado</li>
              <li><strong>Valor:</strong> Quanto você solicitou sacar</li>
              <li><strong>Status:</strong> Estado atual da solicitação</li>
              <li><strong>Observação:</strong> Suas notas ou observações do admin</li>
            </ul>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Você pode clicar no <strong>ícone do olho (👁️)</strong> para ver mais detalhes 
              de qualquer solicitação.
            </p>
          </div>
        </div>
      </section>

      {/* Dicas e Boas Práticas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Recomendações para usar o sistema de saques
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Verifique os Dados Bancários
                </span>
                <p>
                  Sempre <strong>verifique se os dados da conta bancária estão corretos</strong> 
                  antes de solicitar um saque. Saques só podem ser processados para contas em seu nome.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:calendar-clock" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde o Período de Liberação
                </span>
                <p>
                  Lembre-se que parte do seu saldo pode estar <strong>"Em Análise"</strong> aguardando 
                  o período de liberação (D+). Apenas o saldo disponível pode ser sacado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:bell-ring" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acompanhe por Notificações
                </span>
                <p>
                  Você receberá <strong>notificações automáticas</strong> quando seu saque for 
                  aprovado, processado ou rejeitado. Fique atento ao ícone de notificações!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:file-document-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Mantenha o Histórico
                </span>
                <p>
                  O <strong>histórico de saques</strong> é uma ferramenta importante para controle 
                  financeiro. Use-o para acompanhar todas as suas movimentações.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre o sistema de saques
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Qual é o valor mínimo para saque?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  O valor mínimo para solicitar um saque é <strong>R$ 10,00</strong>. 
                  Você não pode solicitar valores menores que este.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Quanto tempo leva para o saque ser processado?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Após a aprovação do administrador, o saque é processado e o valor é transferido 
                  para sua conta bancária. O tempo pode variar conforme o banco, mas geralmente 
                  é processado em até <strong>2 dias úteis</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso cancelar uma solicitação de saque?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Solicitações com status <strong>"Em Análise"</strong> podem ser canceladas. 
                  Após a aprovação, não é mais possível cancelar. Se precisar cancelar, entre 
                  em contato com o suporte.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Por que meu saque foi rejeitado?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Saques podem ser rejeitados por diversos motivos: dados bancários incorretos, 
                  saldo insuficiente, ou questões administrativas. Verifique a <strong>observação</strong> 
                  na solicitação para entender o motivo específico.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso ter mais de uma conta bancária cadastrada?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! Você pode cadastrar <strong>múltiplas contas bancárias</strong> e escolher 
                  qual usar em cada solicitação de saque. Todas devem estar em seu nome.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default SaqueWiki;

